/* eslint-disable no-shadow */
import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import memoize from 'lodash/memoize';

// hooks and contexts
import PathBuilderContext from '../WidgetApp/PathBuilderContext';

// utils and constants
import { ISO_DATE_FORMAT } from '../../../../../ui-kitchen/src/constants/resyCore';
import {
  loadReservations, loadResyConfig, loadVenue, loadVenueInventory,
} from './eventDetailsLoaders';
import Logger from '../../../../../ui-kitchen/src/utils/logger';

// actions, thunks, selectors, reducers
import { selectIsGlobalDiningAccess, setUserLocationLatLong } from '../../../../../ui-kitchen/src/reducers/userSlice';
import { setSelectedCoreSeats, setSelectedCoreSeatsMinMax } from '../../../../../ui-kitchen/src/reducers/resyCoreSlice';
import { getURLSearchParams, setSearchParams } from '../../../../../ui-kitchen/src/utils/searchParamUtils';
import {
  setDay, setReservation, setPartySize, setResyToken, setVenueId, setVenueName,
} from '../../../../../ui-kitchen/src/reducers/bookingSlice';
import { setCountry } from '../../../../../ui-kitchen/src/reducers/countrySlice';
import { getResyConfigVenueThunk } from '../../../../../ui-kitchen/src/reducers/resyConfigSlice';
import { getVenueInventoryThunk } from '../../../../../ui-kitchen/src/reducers/venueInventorySlice';
import { getVenueReservationsThunk } from '../../../../../ui-kitchen/src/reducers/venueReservationsSlice';

// components
import EventPage from './EventPage';
import { getLocationPath, getSearchParamsAsObject } from '../../../../../ui-kitchen/src/utils/locationUtils';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import { getTranslation } from '../../../../../ui-kitchen/src/providers/TranslationProvider';
import {
  setMetadataPageTitle, setMobileFirstLogin, setNavVenueName, setPreviewMode,
} from '../../../../../ui-kitchen/src/reducers/resyRouterSlice';

const EventDetailsController = (props) => {
  const {
    Analytics,
    getResyConfigVenueThunk,
    isAuthenticated,
    isSm,
    maxQuantity,
    selectedDate,
    selectedQuantity,
    setCountry,
    setDay,
    setMetadataPageTitle,
    setMobileFirstLogin,
    setNavVenueName,
    setPartySize,
    setPreviewMode,
    setReservation,
    setResyToken,
    setSelectedCoreSeats,
    setSelectedCoreSeatsMinMax,
    setUserLocationLatLong,
    setVenueId: setVenueIdBooking,
    getVenueInventoryThunk,
    getVenueReservationsThunk,
    setVenueName,
    userIsGda,
    ResyAmexConfig,
  } = props;

  // setup/init
  const searchParams = getURLSearchParams();
  const cancel = searchParams.has('cancel');
  const notifyService = searchParams.get('notifyService');
  const reservationFromSearch = searchParams.get('reservation');
  const resyToken = searchParams.get('resyToken');
  const tableConfigId = searchParams.get('tableConfigId');
  const venueIdFromParams = searchParams.get('venuedId');
  const reservationConfig = getSearchParamsAsObject();

  const { location: locationSlug, venueSlug, eventSlug } = useParams();

  const pathBuilder = useContext(PathBuilderContext);
  pathBuilder.setForceSummary(false);
  pathBuilder.setSearch(selectedQuantity, selectedDate);

  // eslint-disable-next-line no-unused-vars
  const [reservations, setReservations] = useState(null);
  const [venueId, setVenueId] = useState(null);
  const [displayGatingErrorModal, setDisplayGatingErrorModal] = useState(false);

  const navigate = useNavigate();

  const getSelectResy = (venueId) => ({
    // eslint-disable-next-line no-unused-vars
    reservationConfig, reservation, fromOutside, previewMode,
  }) => {
    if (previewMode) {
      // eslint-disable-next-line no-param-reassign
      setPreviewMode(true);
      return;
    }
    if (reservation && isObject(reservation)) {
      setVenueIdBooking(venueId);
      setReservation(reservation);
      setDay(reservation.date);
      setPartySize(reservationConfig?.seats);

      if (resyToken) { setResyToken(resyToken); }

      const setValuesParams = {
        eventPath: `/cities/${locationSlug}/venues/${venueSlug}/events/${eventSlug}`,
        isGda: reservation?.isGda,
        venueId,
      };
      if (!reservation.isEligible && !reservationFromSearch) {
        if (isAuthenticated) {
          setDisplayGatingErrorModal(true);
        } else {
          const nextPath = getLocationPath();
          navigate('/login');
          setSearchParams('next', nextPath);
          setSearchParams('authSuccessRedirectUrl', nextPath);
          setSearchParams('token', reservation.token);
          setSearchParams('exclusiveId', reservation.exclusiveId);
        }
      } else if (reservation.hasMenus) {
        pathBuilder.setValues({ ...setValuesParams, addOnsTemplateId: reservation.templateId });
        navigate(`/venues/${venueId}/add-ons/${reservation.templateId}`);
      } else {
        pathBuilder.setValues({ ...setValuesParams, addOnsTemplateId: undefined });
        navigate('/reservation-details');
      }
    } else if ('notifyService' in reservationConfig) {
      const { notifyService, seats } = reservationConfig;
      if (!isAuthenticated) {
        navigate('/login');
        setSearchParams('notifyService', notifyService);
        setSearchParams('seats', seats);
        setSearchParams('next', getLocationPath());
      }
    }
  };

  const venueSetUp = (venue) => {
    if (!isEmpty(venue)) {
      // eslint-disable-next-line no-unused-vars
      const eventDetailTitle = getTranslation({
        translationKey: 'titles.event_details',
        translationValues: { venueName: venue.name },
        fallback: `Book Your ${venue.venueName} Reservation Now on Resy`,
      });

      setMetadataPageTitle(eventDetailTitle);
      setVenueName(venue.name);
      setNavVenueName(venue.name);
      setMobileFirstLogin(venue.isMobileFirstLogin);
      setCountry(venue.location?.country);
      setUserLocationLatLong(venue.location?.coords);
    }
  };

  // Initialization useEffect
  useEffect(() => {
    let venueIdRef;
    loadVenue({ locationId: locationSlug, venueSlug, venueId: venueIdFromParams })
      .then((venue) => {
        venueIdRef = venue.id;
        venueSetUp(venue);
        setVenueId(venueIdRef);
        pathBuilder.setValues({
          addOnsTemplateId: undefined,
          eventPath: `/cities/${locationSlug}/venues/${venueSlug}/events/${eventSlug}`,
          venueId: undefined,
        });
      })
      .then(() => loadResyConfig({
        getResyConfigVenueThunk, setSelectedCoreSeatsMinMax, venueId: venueIdRef,
      }))
      .then(() => loadVenueInventory({
        getVenueInventoryThunk, isSm, selectedDate, selectedQuantity, venueId: venueIdRef,
      }))
      .then(() => loadReservations({
        getVenueReservationsThunk, maxQuantity, selectedDate, selectedQuantity, setReservations, venueId: venueIdRef,
      }))
      .then(() => {
        const selectResy = getSelectResy(venueIdRef);
        if (notifyService) {
          selectResy({ reservationConfig });
        } else if (reservationFromSearch) {
          selectResy({ reservationConfig, reservation: JSON.parse(reservationFromSearch) });
        } else if (resyToken && !cancel) {
          navigate(`/account/reservations-and-notify/${resyToken}`);
        }
      })
      .catch(() => Logger.error('[CATCH] EventDetailsController intialization useEffect'));
  }, []);

  const selectResy = getSelectResy(venueId);

  // handlers
  const onTicketQuantitySelect = (e) => {
    const quantity = +e.currentTarget.value || 2;
    setSelectedCoreSeats(quantity);
  };

  const closeModal = () => {
    setDisplayGatingErrorModal(false);
  };

  return (
    <EventPage
      Analytics={Analytics}
      context="widget"
      notifyService={notifyService}
      onTicketQuantitySelect={onTicketQuantitySelect}
      resyToken={resyToken}
      seats={selectedQuantity}
      selectResy={selectResy}
      tableConfigId={tableConfigId}
      userIsGda={userIsGda}
      displayGatingErrorModal={displayGatingErrorModal}
      closeModal={closeModal}
      ResyAmexConfig={ResyAmexConfig}
    />
  );
};

EventDetailsController.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  getResyConfigVenueThunk: PropTypes.func.isRequired,
  getVenueReservationsThunk: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isSm: PropTypes.bool.isRequired,
  maxQuantity: PropTypes.number.isRequired,
  selectedDate: PropTypes.instanceOf(Date).isRequired,
  selectedQuantity: PropTypes.number.isRequired,
  setCountry: PropTypes.func.isRequired,
  setDay: PropTypes.func.isRequired,
  setMetadataPageTitle: PropTypes.func.isRequired,
  setMobileFirstLogin: PropTypes.func.isRequired,
  setNavVenueName: PropTypes.func.isRequired,
  setPartySize: PropTypes.func.isRequired,
  setPreviewMode: PropTypes.func.isRequired,
  setReservation: PropTypes.func.isRequired,
  setResyToken: PropTypes.func.isRequired,
  setSelectedCoreSeats: PropTypes.func.isRequired,
  setSelectedCoreSeatsMinMax: PropTypes.func.isRequired,
  setUserLocationLatLong: PropTypes.func.isRequired,
  setVenueId: PropTypes.func.isRequired,
  getVenueInventoryThunk: PropTypes.func.isRequired,
  setVenueName: PropTypes.func.isRequired,
  userIsGda: PropTypes.bool.isRequired,
  ResyAmexConfig: PropTypes.shape({
    baseRequestUrl: PropTypes.string.isRequired,
  }).isRequired,
};

const getDateInstance = memoize((isoDate) => moment(isoDate, ISO_DATE_FORMAT).toDate());
const mapStateToProps = (state) => {
  const isAuthenticated = selectIsAuthenticated(state);
  const userIsGda = selectIsGlobalDiningAccess(state);
  const { matchMedia, resyCore } = state;
  const {
    selectedCoreISODate, selectedCoreSeats, selectedCoreSeatsMax,
  } = resyCore;
  return {
    isAuthenticated,
    isSm: matchMedia.sm,
    maxQuantity: selectedCoreSeatsMax,
    selectedDate: getDateInstance(selectedCoreISODate),
    selectedISODate: selectedCoreISODate,
    selectedQuantity: selectedCoreSeats,
    userIsGda,
  };
};

const mapDispatchToProps = {
  getResyConfigVenueThunk,
  getVenueReservationsThunk,
  setCountry,
  setDay,
  setMetadataPageTitle,
  setMobileFirstLogin,
  setNavVenueName,
  setPartySize,
  setPreviewMode,
  setReservation,
  setResyToken,
  setSelectedCoreSeats,
  setSelectedCoreSeatsMinMax,
  setUserLocationLatLong,
  setVenueId,
  getVenueInventoryThunk,
  setVenueName,
};

export { EventDetailsController as ComponentUnderTest, mapStateToProps };
export default connect(mapStateToProps, mapDispatchToProps)(EventDetailsController);
